import { createSlice } from "@reduxjs/toolkit";
import { NotificationsCountType, NotificationType, PaginatedNotificationsType } from "notifications/types";
import { IFetchNotificationsParams } from "redux/thunks/notifications.thunk";
import { URLSearchParams } from "url";

export type InitialStateAccountType = {
  notifications: {
    count: {
      total: number;
      bid: number;
      order: number;
      isFetching: boolean;
      error: any;
    };
    next: number;
    read: number;
    unread: number;
    data: NotificationType[];
    isFetching: boolean;
    error: any;
  };
};

const initialState: InitialStateAccountType = {
  notifications: {
    count: {
      total: null,
      bid: null,
      order: null,
      isFetching: false,
      error: null,
    },
    next: null,
    read: null,
    unread: null,
    data: null,
    isFetching: null,
    error: null,
  },
};

const accountSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    /** Получение уведомлений площадки ТП */
    fetchNotificationsCountRequest({ notifications }, _action) {
      notifications.count.isFetching = true;
    },
    fetchNotificationsCountSuccess({ notifications }, action: { payload: NotificationsCountType; type: any }) {
      notifications.count.total = action.payload.total;
      notifications.count.bid = action.payload.bid;
      notifications.count.order = action.payload.order;
      notifications.count.isFetching = false;
    },
    fetchNotificationsCountFailure({ notifications }, action) {
      notifications.count.error = action.payload;
      notifications.count.isFetching = false;
    },

    /** Получение уведомлений площадки ТП */
    fetchNotificationsRequest({ notifications }, _action) {
      notifications.isFetching = true;
    },
    fetchNotificationsSuccess(
      { notifications },
      action: {
        payload: {
          data: PaginatedNotificationsType;
          params: IFetchNotificationsParams;
          isUpdate: boolean;
        };
        type: any;
      },
    ) {
      const newDataNotifications: PaginatedNotificationsType = action.payload.data;
    
      let newUrl: URL | null = null;
      const nextUrlString = newDataNotifications?.next;
    
      if (typeof nextUrlString === "string" && nextUrlString?.length > 0) {
        newUrl = new URL(nextUrlString);
      }
    
      const searchParams: URLSearchParams | null = newUrl ? newUrl.searchParams : null;
    
      notifications.next = searchParams ? Number.parseInt(searchParams.get("page") || "") || null : null;
      notifications.read = newDataNotifications.count.read;
      notifications.unread = newDataNotifications.count.unread;
    
      if (action.payload.params.page === 1) {
        if (action.payload.isUpdate) {
          const existingNotifications: NotificationType[] = notifications.data ?? [];
          const filteredNotifications: NotificationType[] = newDataNotifications.results.filter((newItem) => {
            const alreadyExists: boolean = existingNotifications.some((storedItem) => storedItem.id === newItem.id);
            return !alreadyExists;
          });
    
          notifications.data = [...filteredNotifications, ...existingNotifications];
        } else {
          notifications.data = newDataNotifications.results;
        }
      } else {
        const notificationList: NotificationType[] = notifications.data || [];
        notifications.data = [...notificationList, ...newDataNotifications.results];
      }
      
      notifications.isFetching = false;
    },
    
    fetchNotificationsFailure({ notifications }, action) {
      notifications.next = null;
      notifications.error = action.payload;
      notifications.isFetching = false;
    },

    /** Прочитать уведомление площадки ТП */
    readNotificationsRequest({ notifications }, _action) {
      notifications.isFetching = true;
    },
    readNotificationsSuccess({ notifications }, action: { payload: number; type: any }) {
      const notificationList: NotificationType[] = notifications.data || [];

      notifications.data = notificationList?.map((entity: NotificationType) => {
        const updatedNotification: NotificationType = { ...entity, is_read: true };
        const needUpdate: boolean = entity.id === action.payload;

        return needUpdate ? updatedNotification : entity;
      });
      notifications.isFetching = false;
    },
    readNotificationsFailure({ notifications }, _action) {
      notifications.isFetching = false;
    },

    /** Пометка уведомления площадки ТП как непрочитанного */
    restoreNotificationRequest({ notifications }, _action) {
      notifications.isFetching = true;
    },
    restoreNotificationSuccess({ notifications }, action: { payload: number; type: any }) {
      const notificationList: NotificationType[] = notifications.data || [];

      notifications.data = notificationList?.map((entity: NotificationType) => {
        const updatedNotification: NotificationType = { ...entity, is_read: false };
        const needUpdate: boolean = entity.id === action.payload;

        return needUpdate ? updatedNotification : entity;
      });
  
      notifications.isFetching = false;
    },
    restoreNotificationFailure({ notifications }, action) {
      notifications.error = action.payload;
      notifications.isFetching = false;
    },

    /** Прочитать все уведомления площадки ТП */
    readAllNotificationsRequest({ notifications }, _action) {
      notifications.isFetching = true;
    },
    readAllNotificationsSuccess({ notifications }, _action: { payload: string; type: any }) {
      const notificationsList: NotificationType[] = notifications.data;

      notifications.data = notificationsList.map((entity: NotificationType) => {
        const updatedEntity: NotificationType = { ...entity, is_read: true };

        return updatedEntity;
      });

      notifications.isFetching = false;
    },
    readAllNotificationsFailure({ notifications }, action) {
      notifications.error = action.payload;
      notifications.isFetching = false;
    },

    /** Удалить все уведомления площадки ТП */
    deleteAllNotificationsRequest({ notifications }, _action) {
      notifications.isFetching = true;
    },
    deleteAllNotificationsSuccess(state, _action) {
      state.notifications = initialState.notifications;
    },
    deleteAllNotificationsFailure({ notifications }, action) {
      notifications.error = action.payload;
      notifications.isFetching = false;
    },

    /** Удалить все уведомления площадки ТП */
    deleteNotificationRequest({ notifications }, _action) {
      notifications.isFetching = true;
    },
    deleteNotificationSuccess({ notifications }, action: { payload: number; type: any }) {
      const notificationsList: NotificationType[] = notifications.data;

      notifications.data = notificationsList?.filter((entity) => entity.id !== action.payload);
      notifications.isFetching = false;
    },
    deleteNotificationFailure({ notifications }, action) {
      notifications.error = action.payload;
      notifications.isFetching = false;
    },

    /** Очистка уведомлений из редакса */
    clearNotificationsRequest({ notifications }, _action) {
      notifications.isFetching = true;
    },
    clearNotificationsSuccess({ notifications }, _action) {
      notifications.data = null;
      notifications.isFetching = false;
    },
  },
});

const { actions, reducer } = accountSlice;

export const {
  fetchNotificationsCountRequest,
  fetchNotificationsCountSuccess,
  fetchNotificationsCountFailure,

  fetchNotificationsRequest,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,

  readNotificationsRequest,
  readNotificationsSuccess,
  readNotificationsFailure,

  restoreNotificationRequest,
  restoreNotificationSuccess,
  restoreNotificationFailure,

  readAllNotificationsRequest,
  readAllNotificationsSuccess,
  readAllNotificationsFailure,

  deleteAllNotificationsRequest,
  deleteAllNotificationsSuccess,
  deleteAllNotificationsFailure,

  deleteNotificationRequest,
  deleteNotificationSuccess,
  deleteNotificationFailure,

  clearNotificationsRequest,
  clearNotificationsSuccess,
} = actions;

export default reducer;
